import { useState, useEffect } from 'react';
import { IoAddOutline, IoBusinessOutline, IoPeopleOutline, IoTrashOutline, IoPencilOutline, IoCheckmarkOutline, IoCloseOutline, IoPersonAddOutline } from 'react-icons/io5';
import { churchApi } from '../services/api';
import { useMyChurchProgress } from '../hooks/useMyChurchProgress';
import ChurchChallengeSection from '../components/church/ChurchChallengeSection';
import './ChurchManagementPage.css';

const ChurchManagementPage = () => {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingChurch, setEditingChurch] = useState(null);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [showPendingMembers, setShowPendingMembers] = useState(false);
  const [showAdminAssignment, setShowAdminAssignment] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    website: '',
    phone: '',
    email: '',
    allowPublicJoining: true,
    maxMembers: 1000
  });
  const {
    payload: challengePayload,
    loading: challengeLoading,
    error: challengeError,
    retry: retryChallenge,
    status: challengeStatus,
    challenge,
    church: challengeChurch,
    stats: challengeStats
  } = useMyChurchProgress();

  useEffect(() => {
    loadChurches();
  }, []);

  const loadChurches = async () => {
    try {
      setLoading(true);
      const response = await churchApi.getAdministeredChurches();
      setChurches(response.data.churches || []);
    } catch (error) {
      console.error('Error loading churches:', error);
      setError('Failed to load churches');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingMembers = async (churchId) => {
    try {
      const response = await churchApi.getPendingMembers(churchId);
      setPendingMembers(response.data.pendingMembers || []);
    } catch (error) {
      console.error('Error loading pending members:', error);
      setError('Failed to load pending members');
    }
  };

  const loadAvailableAdmins = async (churchId) => {
    try {
      const response = await churchApi.getAvailableAdmins(churchId);
      setAvailableAdmins(response.data.availableUsers || []);
    } catch (error) {
      console.error('Error loading available admins:', error);
      setError('Failed to load available admins');
    }
  };

  const handleCreateChurch = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await churchApi.createChurch(formData);
      setChurches([...churches, response.data.church]);
      setShowCreateForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating church:', error);
      setError(error.response?.data?.message || 'Failed to create church');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChurch = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await churchApi.updateChurch(editingChurch.id, formData);
      setChurches(churches.map(church => 
        church.id === editingChurch.id ? response.data.church : church
      ));
      setEditingChurch(null);
      resetForm();
    } catch (error) {
      console.error('Error updating church:', error);
      setError(error.response?.data?.message || 'Failed to update church');
    } finally {
      setLoading(false);
    }
  };

  const handleEditChurch = (church) => {
    setEditingChurch(church);
    setFormData({
      name: church.name,
      description: church.description || '',
      address: church.address || '',
      website: church.website || '',
      phone: church.phone || '',
      email: church.email || '',
      allowPublicJoining: church.allowPublicJoining,
      maxMembers: church.maxMembers
    });
  };

  const handleViewPendingMembers = async (church) => {
    setSelectedChurch(church);
    setShowPendingMembers(true);
    setShowAdminAssignment(false);
    await loadPendingMembers(church.id);
  };

  const handleViewAdminAssignment = async (church) => {
    setSelectedChurch(church);
    setShowAdminAssignment(true);
    setShowPendingMembers(false);
    await loadAvailableAdmins(church.id);
  };

  const handleApproveMember = async (userId) => {
    try {
      await churchApi.approveMember(selectedChurch.id, userId);
      setPendingMembers(pendingMembers.filter(member => member.id !== userId));
      await loadChurches(); // Refresh church data
    } catch (error) {
      console.error('Error approving member:', error);
      setError('Failed to approve member');
    }
  };

  const handleRejectMember = async (userId) => {
    try {
      await churchApi.rejectMember(selectedChurch.id, userId);
      setPendingMembers(pendingMembers.filter(member => member.id !== userId));
      await loadChurches(); // Refresh church data
    } catch (error) {
      console.error('Error rejecting member:', error);
      setError('Failed to reject member');
    }
  };

  const handleAssignAdmin = async (userId) => {
    try {
      await churchApi.assignAdmin(selectedChurch.id, userId);
      setShowAdminAssignment(false);
      setSelectedChurch(null);
      await loadChurches(); // Refresh church data
    } catch (error) {
      console.error('Error assigning admin:', error);
      setError('Failed to assign admin');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      website: '',
      phone: '',
      email: '',
      allowPublicJoining: true,
      maxMembers: 1000
    });
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingChurch(null);
    setShowPendingMembers(false);
    setShowAdminAssignment(false);
    setSelectedChurch(null);
    resetForm();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading && churches.length === 0) {
    return (
      <div className="church-management-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading churches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="church-management-page">
      <div className="page-header">
        <div className="page-header-content">
          <IoBusinessOutline className="header-icon" />
          <h1>Manage church</h1>
          <p>Create and manage churches you administer</p>
        </div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        <div className="church-management-challenge">
          <h2 className="church-management-challenge-title">Challenge progress</h2>
          {challengeLoading && (
            <p className="church-management-challenge-note">Loading church challenge progress…</p>
          )}
          {challengeError && (
            <div className="church-management-challenge-empty">
              <p>{challengeError}</p>
              <button type="button" className="btn-back" onClick={retryChallenge}>
                Try again
              </button>
            </div>
          )}
          {!challengeLoading && !challengeError && challengeStatus === 'no-challenge' && (
            <p className="church-management-challenge-note">
              No reading challenge is running right now. When one starts, your
              church&apos;s map and still-needed list will appear here.
            </p>
          )}
          {!challengeLoading && !challengeError && challengeStatus === 'no-church' && (
            <p className="church-management-challenge-note">
              Challenge stats are scoped to the church you belong to. Join or
              be approved as a member to see the map and still-needed list.
            </p>
          )}
          {!challengeLoading && !challengeError && challengeStatus === 'ready' && (
            <ChurchChallengeSection
              challenge={challenge}
              church={challengeChurch}
              stats={challengeStats}
              payload={challengePayload}
            />
          )}
        </div>

        {/* Create Church Button */}
        {!showCreateForm && !editingChurch && !showPendingMembers && !showAdminAssignment && (
          <div className="create-church-section">
            <button 
              className="btn-create-church"
              onClick={() => setShowCreateForm(true)}
            >
              <IoAddOutline size={20} />
              Create a church
            </button>
          </div>
        )}

        {/* Create/Edit Church Form */}
        {(showCreateForm || editingChurch) && (
          <div className="church-form-section">
            <h2>{editingChurch ? 'Edit church' : 'Create a church'}</h2>
            
            <form onSubmit={editingChurch ? handleUpdateChurch : handleCreateChurch}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Church name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter church name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Brief description of the church"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Church address"
                    rows="2"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="website">Website</label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="church@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="maxMembers">Maximum members</label>
                  <input
                    type="number"
                    id="maxMembers"
                    name="maxMembers"
                    value={formData.maxMembers}
                    onChange={handleInputChange}
                    min="1"
                    max="10000"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="allowPublicJoining"
                      checked={formData.allowPublicJoining}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark"></span>
                    Allow public joining with join code
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={loading || !formData.name.trim()}
                >
                  {loading ? 'Saving...' : (editingChurch ? 'Update church' : 'Create church')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Pending Members Section */}
        {showPendingMembers && selectedChurch && (
          <div className="pending-members-section">
            <div className="section-header">
              <h2>Pending members — {selectedChurch.name}</h2>
              <button className="btn-back" onClick={handleCancel}>
                Back to churches
              </button>
            </div>
            
            {pendingMembers.length === 0 ? (
              <div className="empty-state">
                <IoPeopleOutline size={48} />
                <h3>No pending members</h3>
                <p>All membership requests have been processed</p>
              </div>
            ) : (
              <div className="pending-members-list">
                {pendingMembers.map(member => (
                  <div key={member.id} className="pending-member-card">
                    <div className="member-info">
                      <h4>{member.name}</h4>
                      <p>{member.email}</p>
                      <span className="request-date">
                        Requested: {new Date(member.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="member-actions">
                      <button 
                        className="btn-approve"
                        onClick={() => handleApproveMember(member.id)}
                      >
                        <IoCheckmarkOutline size={16} />
                        Approve
                      </button>
                      <button 
                        className="btn-reject"
                        onClick={() => handleRejectMember(member.id)}
                      >
                        <IoCloseOutline size={16} />
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin Assignment Section */}
        {showAdminAssignment && selectedChurch && (
          <div className="admin-assignment-section">
            <div className="section-header">
              <h2>Assign admin — {selectedChurch.name}</h2>
              <button className="btn-back" onClick={handleCancel}>
                Back to churches
              </button>
            </div>
            
            <div className="current-admin">
              <h3>Current admin</h3>
              <p>{selectedChurch.admin?.name} ({selectedChurch.admin?.email})</p>
            </div>
            
            <div className="available-admins">
              <h3>Available users</h3>
              {availableAdmins.length === 0 ? (
                <p>No users available for admin assignment</p>
              ) : (
                <div className="admins-list">
                  {availableAdmins.map(admin => (
                    <div key={admin.id} className="admin-card">
                      <div className="admin-info">
                        <h4>{admin.name}</h4>
                        <p>{admin.email}</p>
                      </div>
                      <button 
                        className="btn-assign-admin"
                        onClick={() => handleAssignAdmin(admin.id)}
                      >
                        <IoPersonAddOutline size={16} />
                        Assign as admin
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Churches List */}
        {!showCreateForm && !editingChurch && !showPendingMembers && !showAdminAssignment && (
          <div className="churches-section">
            <h2>Your churches</h2>
            
            {churches.length === 0 ? (
              <div className="empty-state">
                <IoBusinessOutline size={48} />
                <h3>No churches yet</h3>
                <p>Create your first church to get started</p>
              </div>
            ) : (
              <div className="churches-grid">
                {churches.map(church => (
                  <div key={church.id} className="church-card">
                    <div className="church-header">
                      <h3>{church.name}</h3>
                      <div className="church-actions">
                        <button 
                          className="btn-edit"
                          onClick={() => handleEditChurch(church)}
                          title="Edit church"
                        >
                          <IoPencilOutline size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {church.description && (
                      <p className="church-description">{church.description}</p>
                    )}
                    
                    <div className="church-details">
                      {church.address && (
                        <div className="detail-item">
                          <span className="detail-label">Address:</span>
                          <span className="detail-value">{church.address}</span>
                        </div>
                      )}
                      
                      {church.website && (
                        <div className="detail-item">
                          <span className="detail-label">Website:</span>
                          <a href={church.website} target="_blank" rel="noopener noreferrer" className="detail-value">
                            {church.website}
                          </a>
                        </div>
                      )}
                      
                      {church.phone && (
                        <div className="detail-item">
                          <span className="detail-label">Phone:</span>
                          <span className="detail-value">{church.phone}</span>
                        </div>
                      )}
                      
                      {church.email && (
                        <div className="detail-item">
                          <span className="detail-label">Email:</span>
                          <span className="detail-value">{church.email}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="church-stats">
                      <div className="stat-item">
                        <IoPeopleOutline size={16} />
                        <span>{church.members?.length || 0} members</span>
                      </div>
                      {church.pendingMembersCount > 0 && (
                        <div className="stat-item pending">
                          <span>{church.pendingMembersCount} pending</span>
                        </div>
                      )}
                      <div className="stat-item">
                        <span className="join-code">Join code: {church.joinCode}</span>
                      </div>
                    </div>
                    
                    <div className="church-settings">
                      <span className={`setting-badge ${church.allowPublicJoining ? 'enabled' : 'disabled'}`}>
                        {church.allowPublicJoining ? 'Public joining' : 'Private'}
                      </span>
                      <span className="setting-badge">
                        Max: {church.maxMembers}
                      </span>
                    </div>

                    <div className="church-management-actions">
                      {church.pendingMembersCount > 0 && (
                        <button 
                          className="btn-pending-members"
                          onClick={() => handleViewPendingMembers(church)}
                        >
                          <IoPeopleOutline size={16} />
                          Review pending ({church.pendingMembersCount})
                        </button>
                      )}
                      <button 
                        className="btn-assign-admin"
                        onClick={() => handleViewAdminAssignment(church)}
                      >
                        <IoPersonAddOutline size={16} />
                        Assign admin
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChurchManagementPage;
